import { z } from "zod";

/**
 * The simulation's three answers, **one contract for the doors and for every client** (`GENERIC-DOOR-SWEEP`, Arc 2, A48).
 *
 * `POST /api/v1/simulation/quote`, `GET /api/v1/simulations/{id}` and `GET /api/v1/simulation/config` answered
 * `Promise<unknown>`, and client-web typed the quote three ways (`Partial<Estimate>`, `{ amountFinanced?: number }`,
 * `unknown`). The quote and the stored simulation answer the SAME shape, by the stated test of `sim-quote-config`; the
 * config is the settings row in force.
 *
 * **Every `*Amount` is whole FCFA** (`Int` in the platform's tables, `Money = number` in the module's port): a float or a
 * string where an amount stands fails the integration spec that parses the real answer, which is the point. The three
 * `*Pct` fields are percentages and carry decimals (`12.5`), they are not money.
 */
export const quoteAnswerSchema = z.strictObject({
  /** What the client is quoted: capital and interest plus insurance, rounded once. */
  instalmentAmount: z.number().int(),
  capitalAmount: z.number().int(),
  insuranceAmount: z.number().int(),
  /** The price less the deposit, plus the file fee when one came with the question. */
  amountFinanced: z.number().int(),
  depositPct: z.number(),
  durationMonths: z.number().int(),
  nominalRatePct: z.number(),
  /** Which settings row produced it, so an old quote stays explainable. */
  settingsVersion: z.number().int(),
  /** The estimate a buyer sees before an offer: capital and interest with the fee carried inside the loan. */
  estimateAmount: z.number().int(),
  /** The fee carried inside `amountFinanced`; `0` when none came. */
  fileFeeAmount: z.number().int(),
  depositAmount: z.number().int(),
  vehicleFinancedAmount: z.number().int(),
});

/**
 * `POST /simulation/quote` when the body carried `fileFeeAmount`: the same quote and, beside it, what the fee costs over the
 * term. **`null` is the door's own answer** when no settings are in force or one of the two plans cannot be quoted
 * (`extraInterestOnFinancedFees`); a client prints a dash for it, never a zero.
 */
export const quoteWithFeeAnswerSchema = quoteAnswerSchema.extend({
  extraInterestAmount: z.number().int().nullable(),
});

/** `GET /api/v1/simulation/config`: the settings in force on the day, cached five minutes. */
const buildSimulationSettings = (object: typeof z.strictObject) =>
  object({
    version: z.number().int(),
    referenceDepositPct: z.number(),
    referenceDurationMonths: z.number().int(),
    referenceRatePct: z.number(),
    borrowerInsuranceRatePct: z.number(),
    effortCeilingPct: z.number(),
    effortComfortPct: z.number(),
    /**
     * `CAPACITY-ESTIMATE` (the architect's ruling of 1 November): **the client's own limits, served from ONE source** (`CAPACITY_DEPOSIT_PCT_MIN/MAX` and `CAPACITY_DURATION_MONTHS_MIN/MAX` in `capacity-estimate.ts`),
     * so a screen draws what is served and the dealer's estimate reads the same numbers. Additive.
     */
    depositMinPct: z.number().int(),
    depositMaxPct: z.number().int(),
    durationMinMonths: z.number().int(),
    durationMaxMonths: z.number().int(),
  });
export const simulationSettingsAnswerSchema = buildSimulationSettings(z.strictObject);
/** The apps' reading of the same answer: unknown keys tolerated. */
export const simulationSettingsLenientReadSchema = buildSimulationSettings(z.looseObject);

export type QuoteAnswer = z.infer<typeof quoteAnswerSchema>;
export type QuoteWithFeeAnswer = z.infer<typeof quoteWithFeeAnswerSchema>;
export type SimulationSettingsAnswer = z.infer<typeof simulationSettingsAnswerSchema>;
